import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import RecruitmentShell from "../components/RecruitmentShell";
import { useResponseSignature } from "../hooks/useResponseSignature";
import { resolveHiringCompanyName } from "../lib/responseSettings";

const SIGNATURE_MAX_CHARS = 240;

export default function ResponseSettingsSignatureEditPage({ currentUser, setCurrentUser, authToken }) {
  const navigate = useNavigate();
  const companyName = resolveHiringCompanyName(currentUser);
  const { savedSignature, defaultSignature, commitSignature } = useResponseSignature(authToken, companyName);
  const [saving, setSaving] = useState(false);

  const [savedBaseline, setSavedBaseline] = useState(savedSignature);
  const [draft, setDraft] = useState(savedSignature);

  useEffect(() => {
    setSavedBaseline(savedSignature);
    setDraft(savedSignature);
  }, [savedSignature]);

  const isDirty = useMemo(() => draft !== savedBaseline, [draft, savedBaseline]);

  const handleSave = useCallback(
    async (event) => {
      event.preventDefault();
      if (!isDirty || saving) return;
      setSaving(true);
      try {
        await commitSignature(draft);
        navigate("/response-settings");
      } finally {
        setSaving(false);
      }
    },
    [commitSignature, draft, isDirty, saving, navigate]
  );

  const handleReset = useCallback(() => {
    setDraft(savedBaseline);
  }, [savedBaseline]);

  const handleCancel = useCallback(() => {
    navigate("/response-settings");
  }, [navigate]);

  return (
    <RecruitmentShell currentUser={currentUser} setCurrentUser={setCurrentUser}>
      <main className="nd1-shell nd1-recruitment-profile-main jw-recruitment-main recruitment-profile-page response-settings-page response-settings-edit-page">
        <div className="recruitment-profile-shell nd1-recruitment-profile-shell">
          <header className="recruitment-profile-masthead nd1-recruitment-profile-masthead">
            <Link className="back-link nd1-recruitment-profile-back" to="/response-settings">
              &larr; Back to response settings
            </Link>
            <p className="eyebrow nd1-label nd1-recruitment-profile-eyebrow">Signature</p>
            <h1>Signature</h1>
            <p className="subtitle">Sign-off added to rejection notifications when you email an applicant.</p>
          </header>

          <div className="recruitment-profile-content profile-manage-card nd1-recruitment-profile-content">
            <form
              id="response-settings-signature-edit-form"
              className="recruitment-profile-stack recruitment-profile-stack--settings response-settings-stack"
              onSubmit={handleSave}
            >
              <section className="recruitment-profile-surface response-settings-signature-edit">
                <label className="response-settings-signature-field">
                  <span className="job-search-label">Sign-off</span>
                  <textarea
                    className="response-settings-signature-input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={4}
                    maxLength={SIGNATURE_MAX_CHARS}
                    placeholder={defaultSignature}
                  />
                </label>
              </section>
            </form>

            <div
              className={`job-actions-float nd1-recruitment-profile-footer${isDirty ? " job-actions-float--unsaved" : ""}`}
              role="toolbar"
              aria-label={isDirty ? "Signature save actions, unsaved changes" : "Signature save actions"}
            >
              {isDirty ? (
                <p className="profile-manage-unsaved-hint" role="status">
                  Changes are not saved.
                </p>
              ) : null}
              <div className="job-actions-float-row">
                <div className="job-actions-buttons">
                  <button
                    type="submit"
                    form="response-settings-signature-edit-form"
                    className="job-action-btn primary"
                    disabled={!isDirty}
                  >
                    Save changes
                  </button>
                  <button
                    type="button"
                    className="job-action-btn"
                    disabled={!isDirty}
                    aria-label="Discard unsaved changes"
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                  <button type="button" className="job-action-btn" onClick={handleCancel}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </RecruitmentShell>
  );
}
